// One-level-at-a-time undo for destructive actions.
//
// Box delete, bank-tile delete and editor Clear were all irreversible, and
// their ✕ buttons sit right next to controls you press constantly — which
// makes experimenting risky in a tool whose whole point is experimenting.
//
// Deliberately snapshot-based rather than command-based: the entire project
// and palette are plain JSON, so cloning them is cheap and *cannot* drift out
// of sync with the live state the way hand-written inverse operations do. A
// wrong undo is worse than no undo.

export interface UndoEntry {
  /** Shown on the button, e.g. "Delete box A". */
  label: string;
  restore: () => void;
}

export class UndoStack {
  private entries: UndoEntry[] = [];

  /** @param limit how many steps to keep — snapshots are small, but unbounded
   *  growth on a long session is still waste.
   *  @param onChange fired whenever the stack's depth changes, so the UI can
   *  show/hide the button. */
  constructor(
    private readonly limit = 20,
    private readonly onChange?: () => void,
  ) {}

  push(label: string, restore: () => void): void {
    this.entries.push({ label, restore });
    if (this.entries.length > this.limit) this.entries.shift();
    this.onChange?.();
  }

  /** Undo the most recent action. Returns its label, or null if empty. */
  undo(): string | null {
    const entry = this.entries.pop();
    this.onChange?.();
    if (!entry) return null;
    entry.restore();
    return entry.label;
  }

  get canUndo(): boolean {
    return this.entries.length > 0;
  }

  /** Label of what would be undone next — for the button's tooltip. */
  get topLabel(): string | null {
    return this.entries.length ? this.entries[this.entries.length - 1].label : null;
  }

  get depth(): number {
    return this.entries.length;
  }

  clear(): void {
    this.entries.length = 0;
    this.onChange?.();
  }
}
